import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { seekAds, tokens } from '../utils/api';
import Alert from '../components/Alert';
import LocationSelector from '../components/LocationSelector';
import toast from 'react-hot-toast';

const CATEGORIES = ['Technical', 'Domestic', 'Freelance', 'Tutor', 'Creative', 'Local Task'];
const RATE_TYPES = [
  { value: 'HOURLY', label: 'Hourly (per hour)' },
  { value: 'DAILY', label: 'Daily (per day)' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'FIXED', label: 'Fixed (one-time)' },
];
const SEEK_COST = 3;

export default function PostSeekAd() {
  const { t, user, fetchBalance } = useApp();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  const [form, setForm] = useState({
    title: '', bio: '', category: 'Technical',
    skills: [], location: '', rate: '', rateType: 'MONTHLY',
    experience: '', availability: '', portfolioUrl: '',
  });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'SEEKER') { navigate('/dashboard'); return; }
    tokens.balance().then(r => setBalance(r.balance)).catch(() => { });
  }, [user, navigate]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !form.skills.includes(s)) setForm(f => ({ ...f, skills: [...f.skills, s] }));
    setSkillInput('');
  };
  const removeSkill = s => setForm(f => ({ ...f, skills: f.skills.filter(x => x !== s) }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (balance < SEEK_COST) { toast.error(`Need ${SEEK_COST} tokens. You have ${balance}.`); return; }
    if (form.skills.length === 0) { toast.error('Add at least one skill.'); return; }
    setLoading(true);
    try {
      await seekAds.create({ ...form, skills: form.skills });
      fetchBalance();
      toast.success(t('seek_ad_posted'));
      navigate('/dashboard?tab=seek');
    } catch (err) {
      toast.error(err.error || t('error_occurred'));
    } finally { setLoading(false); }
  };

  return (
    <div className="container-sm" style={{ paddingTop: '36px', paddingBottom: '60px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>{t('post_seek')}</h1>
      <p style={{ color: 'var(--text3)', fontSize: '14px', marginBottom: '24px' }}>
        Publish your availability and skills so employers can find and hire you directly.
      </p>

      {/* Token cost banner */}
      <Alert
        type={balance !== null && balance < SEEK_COST ? 'error' : 'info'}
        message={
          balance !== null && balance < SEEK_COST
            ? `Insufficient tokens. Publishing costs ${SEEK_COST} tokens, you have ${balance}.`
            : `Publishing a seek ad costs ${SEEK_COST} tokens. Your balance: ${balance ?? '…'} tokens.`
        }
      />

      <form className="card" onSubmit={handleSubmit} style={{ marginTop: '16px' }}>
        <div className="form-group">
          <label className="form-label">Ad title — What's your role? *</label>
          <input className="form-input" required value={form.title} onChange={set('title')}
            placeholder="e.g. Experienced React Developer available for hire" />
        </div>

        <div className="form-group">
          <label className="form-label">{t('bio')} — Describe yourself *</label>
          <textarea className="form-input" required rows={5} value={form.bio} onChange={set('bio')}
            placeholder="Describe your background, what you do best, past work, what you're looking for..."
            style={{ resize: 'vertical' }} />
        </div>

        <div className="form-group">
          <label className="form-label">{t('category')} *</label>
          <select className="form-input" value={form.category} onChange={set('category')}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="form-group">

          <label className="form-label">{t('location')} *</label>
          <LocationSelector
            initialValue={form.location}
            onChange={val => setForm(f => ({ ...f, location: val }))}
          />
        </div>


        {/* Skills */}
        <div className="form-group">
          <label className="form-label">{t('skills')} *</label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input className="form-input" style={{ flex: 1 }}
              value={skillInput} onChange={e => setSkillInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
              placeholder="Type a skill and press Enter (e.g. JavaScript)" />
            <button type="button" className="btn btn-outline btn-sm" onClick={addSkill}>Add</button>
          </div>
          {form.skills.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {form.skills.map(s => (
                <span key={s} className="chip chip-teal" style={{ gap: '4px', cursor: 'default' }}>
                  {s}
                  <button type="button" onClick={() => removeSkill(s)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--brand-dark)', padding: '0 2px', lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Rate */}
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">Your rate (৳) *</label>
            <input className="form-input" type="number" required min="1" value={form.rate} onChange={set('rate')}
              placeholder="e.g. 5000" />
          </div>
          <div className="form-group">
            <label className="form-label">Rate type *</label>
            <select className="form-input" value={form.rateType} onChange={set('rateType')}>
              {RATE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t('experience')} (optional)</label>
          <input className="form-input" value={form.experience} onChange={set('experience')}
            placeholder="e.g. 3 years in web development" />
        </div>

        <div className="form-group">
          <label className="form-label">{t('availability')} (optional)</label>
          <input className="form-input" value={form.availability} onChange={set('availability')}
            placeholder="e.g. Full-time, available from next week" />
        </div>

        <div className="form-group">
          <label className="form-label">{t('portfolio')} URL (optional)</label>
          <input className="form-input" type="url" value={form.portfolioUrl} onChange={set('portfolioUrl')}
            placeholder="https://your-portfolio.com" />
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-full"
            disabled={loading || (balance !== null && balance < SEEK_COST)}>
            {loading
              ? <span className="spinner" style={{ width: 16, height: 16 }} />
              : `${t('post_seek')} (−${SEEK_COST} ${t('tokens')})`}
          </button>
        </div>
      </form>
    </div>
  );
}
